import { supabase } from "./supabase";
import { TZ } from "./format";

/** [lng, lat, epochMs, kmh, course] */
export type HistPoint = [number, number, number, number, number];

export type Trip = {
  start: string; end: string; distanceKm: number; durationMin: number;
  maxKmh: number; avgKmh: number; startLatLng: [number, number]; endLatLng: [number, number];
};
export type Stop = { start: string; end: string; durationMin: number; lat: number; lng: number };

export type History = {
  device: { id: string; name: string };
  from: string; to: string;
  points: HistPoint[]; dropped: number; rawCount: number;
  /** positions held back by the GPS-quality rules: [lng, lat, epochMs] */
  suspect?: [number, number, number][];
  noFix?: number;
  trips: Trip[]; stops: Stop[];
};

/** Fiji is UTC+12 (no daylight saving since 2022). */
export const FIJI_OFFSET = "+12:00";

export function fijiToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date());
}

export function shiftDay(day: string, delta: number): string {
  const d = new Date(`${day}T12:00:00${FIJI_OFFSET}`);
  d.setUTCDate(d.getUTCDate() + delta);
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(d);
}

export function dayRange(day: string) {
  const from = new Date(`${day}T00:00:00${FIJI_OFFSET}`);
  const to = new Date(from.getTime() + 24 * 3600 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Short Fiji weekday + date + time, e.g. "Mon 06 Oct 08:12" (for multi-day views). */
export function fijiDayClock(ms: number | string) {
  const d = new Date(ms);
  const day = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, weekday: "short", day: "2-digit", month: "short" }).format(d);
  return `${day} ${fijiClock(ms)}`;
}

/** History for an explicit time range (ISO strings). */
export async function fetchHistoryRange(deviceId: string, from: string, to: string): Promise<History> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Please sign in again.");
  const qs = new URLSearchParams({ device: deviceId, from, to });
  const r = await fetch(`/api/history?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.error ?? `History failed (${r.status})`);
  return body as History;
}

/** History for `span` Fiji days ending on `day` (max 7, matching the server limit). */
export async function fetchHistory(deviceId: string, day: string, span = 1): Promise<History> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Please sign in again.");
  const from = dayRange(shiftDay(day, -(Math.min(Math.max(span, 1), 7) - 1))).from;
  const { to } = dayRange(day);
  const qs = new URLSearchParams({ device: deviceId, from, to });
  const r = await fetch(`/api/history?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.error ?? `History failed (${r.status})`);
  return body as History;
}

export function fijiClock(ms: number | string) {
  return new Intl.DateTimeFormat("en-FJ", {
    timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(ms));
}

export function fmtDuration(min: number) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Index of the last point at or before time t (points sorted by time). */
export function indexAt(points: HistPoint[], t: number) {
  let lo = 0, hi = points.length - 1;
  if (hi < 0 || t <= points[0][2]) return 0;
  if (t >= points[hi][2]) return hi;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (points[mid][2] <= t) lo = mid; else hi = mid - 1;
  }
  return lo;
}

/** Interpolated [lng, lat, kmh, course] at time t. */
export function positionAt(points: HistPoint[], t: number): [number, number, number, number] {
  const i = indexAt(points, t);
  const a = points[i], b = points[i + 1];
  if (!b || b[2] === a[2]) return [a[0], a[1], a[3], a[4]];
  const f = Math.min(1, Math.max(0, (t - a[2]) / (b[2] - a[2])));
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, Math.round(a[3] + (b[3] - a[3]) * f), a[4]];
}

// ---------------------------------------------------------------- data log (V3.4.0)
export type LogRow = { t: number; server: number | null; lat: number; lng: number; alt: number | null; kmh: number; course: number;
  valid: boolean; status: "ok" | "unconfirmed" | "no_fix" | "duplicate"; attrs: Record<string, unknown> };
export type DataLog = { total: number; truncated: boolean; rows: LogRow[] };

export async function fetchDataLog(deviceId: string, from: string, to: string): Promise<DataLog> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Please sign in again.");
  const qs = new URLSearchParams({ device: deviceId, from, to, detail: "1" });
  const r = await fetch(`/api/history?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.error ?? `Data log failed (${r.status})`);
  return body as DataLog;
}
